import { GENERAL_SECTION } from "../fields";

const SECTION_CATALOG = [
    {
        key: "specification",
        label: "Specification",
        description: "The test listing, with its limits and method references.",
    },
    {
        key: "protocol",
        label: "Test Schedule (Protocol)",
        description: "The test schedule and its notes.",
    },
    {
        key: "report",
        label: "Study Results (Report)",
        description: "The study tables.",
    },
    {
        key: "container",
        label: "Container Closure System",
        description: "The container-closure system and the proposed shelf life.",
    },
];

const SECTIONS = SECTION_CATALOG.map((s) => s.key);

const STEPS = [
    { key: "general", title: "Stability" },
    { key: "details", title: "Configure" },
];

const BLOCK_TITLES = {
    "general:stability": "Stability",
};

const BLOCK_DESCRIPTIONS = {};

// Which §3.2.P.8 parts read each field. The form is the same for all three; only the
// required marker changes with the part being generated.
const P81 = "3.2.P.8.1";
const P82 = "3.2.P.8.2";
const P83 = "3.2.P.8.3";

const usedFor = (parts) => `Used for ${parts.join(", ")}.`;

const FIELD_DEFINITIONS = {
    product_names: {
        block: "stability",
        label: "Product Name(s)",
        type: "string_list",
        description: "The products the study covers.",
        usedFor: usedFor([P81, P82, P83]),
    },
    test_conditions: {
        block: "stability",
        label: "Test Conditions",
        type: "string_list",
        description:
            'The storage conditions under test, e.g. "25°C/60%RH". Only studies run under these conditions are captured.',
        usedFor: usedFor([P81, P82, P83]),
    },
    purpose_of_study: {
        block: "stability",
        label: "Purpose of Study",
        type: "long_text",
        description:
            "The study's scope statement, a paragraph, rendered into the document near-verbatim — write it as it should read there. 3.2.P.8.1 repeats it in its batch table.",
        usedFor: usedFor([P81, P83]),
    },
    addition_tests: {
        block: "stability",
        label: "Additional Tests",
        type: "string_list",
        description: "Tests beyond those the specification lists.",
        usedFor: usedFor([P81, P82, P83]),
    },
    product_type: {
        block: "stability",
        label: "Submission Type",
        type: "text",
        description:
            'Specify how the regulatory filing should be referenced in the generated text, e.g. "This variation contains stability data...".',
        usedFor: usedFor([P82]),
    },
    company_name: {
        block: "stability",
        label: "Company Name",
        type: "text",
        usedFor: usedFor([P82]),
    },
    extension_of_expiration_statement: {
        block: "stability",
        label: "Extension of Expiration Dating Period",
        type: "long_text",
        description:
            "The extension-of-expiration commitment. Becomes its own section — leave it blank and that section is not rendered at all.",
        usedFor: usedFor([P82]),
    },
};

const stabilityTemplate = (id, required) => ({
    id,
    steps: STEPS,
    sectionCatalog: SECTION_CATALOG,
    fields: Object.entries(FIELD_DEFINITIONS).map(([name, definition]) => ({
        section: GENERAL_SECTION,
        group: "p8_stability",
        name,
        ...definition,
        required: required.includes(name),
    })),
    blockTitles: BLOCK_TITLES,
    blockSections: {},
    blockDescriptions: BLOCK_DESCRIPTIONS,
    sectionArticleType: {},
    sectionsWithoutDocuments: new Set(),
    optionalSections: new Set(["container"]),
    orderedSectionKey: null,
    nonMetadataKeys: new Set(),
    fixedSections: SECTIONS,
    payloadTables: ["p8_stability"],
    emitsDeviceFamily: false,
    hasArticles: false,
    advisories: null,
});

// Each part's required fields; must match the backend's P8 templates.
export const p8StabilitySummary = stabilityTemplate("p8_stability_summary", [
    "product_names",
    "test_conditions",
]);

export const p8StabilityProtocol = stabilityTemplate("p8_stability_protocol", [
    "product_names",
    "test_conditions",
    "product_type",
    "company_name",
]);

export const p8StabilityData = stabilityTemplate("p8_stability_data", [
    "product_names",
    "test_conditions",
    "purpose_of_study",
]);
